import { call, put, select } from 'redux-saga/effects'
import {
  http_require_quote,
  http_resolve_agencies,
  http_resolve_document,
  http_resolve_fpool_general,
  http_resolve_otherdoc,
  http_resolve_otherdocs,
  http_resolve_parameters,
  http_resolve_products,
  http_resolve_quotedoc,
  http_resolve_residence_municipalities,
  http_resolve_vehicle_brands,
  http_resolve_vehicle_models,
  http_resolve_vehicle_versions,
  http_send_quotedoc,
  http_send_support_agency,
} from 'src/http'
import {
  agency_t,
  cookie_informative,
  fitem_rd,
  municipality_t,
  option_t,
  otherdoc_list_t,
  otherdoc_t,
  parameter_t,
  product_t,
  quote_t,
  quotedoc_t,
  saga_act,
  vehicle_brand_t,
  vehicle_model_t,
  vehicle_version_t,
} from 'src/types'
import { get_language, sort_fpool, to_formik_item } from 'src/utils'

import { shoot } from '../actions'
import {
  select_company,
  select_effective_date,
  select_history_item,
  select_start_date,
} from '../store'
import { saga_do_request } from './base.saga'
import { saga_resolve_fpool } from './common.saga'

/**
 * Resolve fpool general
 */
export function* saga_resolve_fpool_general(action: saga_act): unknown {
  const fpool: fitem_rd[] = yield call(saga_do_request, http_resolve_fpool_general, action)
  if (fpool) {
    yield put(shoot.reduce_formik_pool(sort_fpool(fpool?.map(to_formik_item))))
  }
}

/**
 * Resolve fpool special
 */
export function* saga_resolve_fpool_special(action: saga_act): unknown {
  const sector: string = yield select(select_history_item('sector'))
  const entity: string = yield select(select_history_item('entity'))
  const effective_date: string = yield select(select_history_item('effective_date'))
  const company: option_t = yield select(select_company)
  const startDate: string = yield select(select_start_date)
  const effectiveDate = yield select(select_effective_date)

  if (
    action.args.sector !== sector ||
    action.args.entity !== entity ||
    effectiveDate !== effective_date
  ) {
    yield put(shoot.reduce_history_item('sector', action.args.sector))
    yield put(shoot.reduce_history_item('entity', action.args.entity))
    yield put(shoot.reduce_history_item('effective_date', effectiveDate))
    yield call(saga_resolve_fpool, {
      ...action,
      args: {
        ...action?.args,
        company: company?.key,
        channel: 'PREVCONN',
        startDate,
        language: get_language(),
        ...(effectiveDate && { effectiveDate }),
      },
    })
  }
}

/**
 * Resolve products
 */
export function* saga_resolve_products(action: saga_act): unknown {
  const products: product_t[] = yield call(saga_do_request, http_resolve_products, action)
  if (products) {
    yield put(shoot.reduce_products(products))
  }
}

/**
 * Resolve vehicle brands
 */
export function* saga_resolve_vehicle_brands(action: saga_act): unknown {
  const brands: vehicle_brand_t[] = yield call(saga_do_request, http_resolve_vehicle_brands, action)
  if (brands) {
    if (action.args.flg4R2R === '4R') {
      yield put(shoot.reduce_vehicle_brands(brands))
    } else if (action.args.flg4R2R === '2R') {
      yield put(shoot.reduce_vehicle_mop_brands(brands))
    }
  }
}

/**
 * Resolve vehicle models
 */
export function* saga_resolve_vehicle_models(action: saga_act): unknown {
  const models: vehicle_model_t[] = yield call(saga_do_request, http_resolve_vehicle_models, action)
  if (models) {
    if (action.args.flg4R2R === '4R') {
      yield put(shoot.reduce_vehicle_models(models))
    } else if (action.args.flg4R2R === '2R') {
      yield put(shoot.reduce_vehicle_mop_models(models))
    }
  }
}

/**
 * Resolve vehicle versions
 */
export function* saga_resolve_vehicle_versions(action: saga_act): unknown {
  const versions: vehicle_version_t[] = yield call(
    saga_do_request,
    http_resolve_vehicle_versions,
    action
  )
  if (versions) {
    yield put(shoot.reduce_vehicle_versions(versions))
  }
}

/**
 * Resolve residence municipalities
 */
export function* saga_resolve_residence_municipalities(action: saga_act): unknown {
  const municipalities: municipality_t[] = yield call(
    saga_do_request,
    http_resolve_residence_municipalities,
    action
  )
  if (municipalities) {
    yield put(shoot.reduce_residence_municipalities(municipalities))
  }
}

/**
 * Require post
 */
export function* saga_require_quote(action: saga_act): unknown {
  const quote: quote_t = yield call(saga_do_request, http_require_quote, action)
  if (quote) {
    yield put(shoot.reduce_quote(quote))
  }
}

/**
 * Resolve agencies
 */
export function* saga_resolve_agencies(action: saga_act): unknown {
  const agencies: agency_t[] = yield call(saga_do_request, http_resolve_agencies, action)
  yield put(shoot.reduce_agencies(agencies || []))
}

/**
 * Send support agency
 */
export function* saga_send_support_agency(action: saga_act): unknown {
  const outcome: string = yield call(saga_do_request, http_send_support_agency, action)
  if (outcome) {
    yield put(shoot.reduce_outcome_support_agency(outcome))
  }
}

/**
 * Resolve quote document
 */
export function* saga_resolve_quotedoc(action: saga_act): unknown {
  const doc: quotedoc_t = yield call(saga_do_request, http_resolve_quotedoc, action)
  if (doc) {
    yield put(shoot.reduce_quotedoc(doc))
  }
}

/**
 * Send quote document - email
 */
export function* saga_send_quotedoc(action: saga_act): unknown {
  const outcome: string = yield call(saga_do_request, http_send_quotedoc, action)
  if (outcome) {
    yield put(shoot.reduce_outcome_quotedoc(outcome))
  }
}

/**
 * Resolve other documents
 */
export function* saga_resolve_otherdocs(action: saga_act): unknown {
  const docs: otherdoc_list_t[] = yield call(saga_do_request, http_resolve_otherdocs, action)
  if (docs) {
    yield put(shoot.reduce_otherdocs(docs))
  }
}

/**
 * Resolve other document
 */
export function* saga_resolve_otherdoc(action: saga_act): unknown {
  const doc: otherdoc_t = yield call(saga_do_request, http_resolve_otherdoc, action)
  if (doc) {
    yield put(shoot.reduce_otherdoc(doc))
  }
}

/**
 * Resolve parameters
 */
export function* saga_resolve_parameters(action: saga_act): unknown {
  const parameters: parameter_t[] = yield call(saga_do_request, http_resolve_parameters, action)
  if (parameters) {
    if (action?.args?.domain === 'COOKIE_POLICY' && action?.args?.subdomain === 'Informativa') {
      const cookie_informatives: cookie_informative[] = parameters
        ?.map((parameter) => ({
          id: parameter?.valore_1,
          content: parameter?.contenuto || '',
        }))
        ?.sort((a, b) => {
          const id_a = +(a?.id?.split('#')[1] || '')
          const id_b = +(b?.id?.split('#')[1] || '')
          return id_a < id_b ? -1 : id_a > id_b ? 1 : 0
        })
      yield put(shoot.reduce_cookie_informatives(cookie_informatives))
    } else if (
      action?.args?.domain === 'DOCUMENTO' &&
      action?.args?.subdomain === 'Accessibilita'
    ) {
      const documents: parameter_t[] = parameters
      yield put(shoot.reduce_documents(documents))
    }
  }
}

/**
 * Resolve Generic document
 */
export function* saga_resolve_document(action: saga_act): unknown {
  const documents: parameter_t[] = yield call(saga_do_request, http_resolve_document, action)
  if (documents) {
    yield put(shoot.reduce_document(documents[0]))
  }
}
